import React, { createContext, useContext, useMemo, useState } from "react";

import invariant from "tiny-invariant";

import { TeamRatings, TeamTypes } from "../types/coachreview";
import {
    CoachReviewContextProps,
    CoachReviewContextValues,
} from "../types/contexts";

import { useAppSelector } from "../../../../hooks/reduxHooks";
import { selectUser } from "../../../../features/auth/authSlice";

const CoachReviewContext = createContext<CoachReviewContextValues | null>(null);

export const useCoachReviewContext = () => {
    const context = useContext(CoachReviewContext);
    if (!context) {
        throw new Error(
            "useCoachReviewContext must be used within a CoachReviewProvider",
        );
    }
    return context;
};

export const CoachReviewProvider: React.FC<CoachReviewContextProps> = props => {
    const { children, ratings, game, formMethods } = props;
    const { setValue } = formMethods;
    const user = useAppSelector(selectUser);
    invariant(user);

    const [tempRating, setTempRating] = useState<
        Record<TeamTypes, TeamRatings | undefined>
    >({
        home: ratings.find(
            r => r.team.id === game.home.id && r.ratedBy.id === user.id,
        )?.ratings,
        visitor: ratings.find(
            r => r.team.id === game.visitor.id && r.ratedBy.id === user.id,
        )?.ratings,
    });

    const handleStarClick = (
        team: TeamTypes,
        rating: keyof TeamRatings,
        idx: number,
    ) => {
        setTempRating(prev => ({
            ...prev,
            [team]: {
                ...prev[team],
                [rating]: idx,
            },
        }));
        setValue(`${team}.${rating}`, idx, { shouldDirty: true });
    };

    const contextValue = useMemo(
        () => ({
            game,
            tempRating,
            setTempRating,
            formMethods,
            handleStarClick,
        }),
        [tempRating],
    );

    return (
        <CoachReviewContext.Provider value={contextValue}>
            {children}
        </CoachReviewContext.Provider>
    );
};
